import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const projects = [
  {
    title: "E-Commerce System",
    tech: "Laravel + MySQL",
    overview:
      "A full-featured e-commerce platform with product management, cart, and order processing.",
    features: [
      "Product & category management",
      "User authentication",
      "Shopping cart & checkout",
      "Admin dashboard",
    ],
    status: "Completed",
  },
  {
    title: "E-Learning Website",
    tech: "React + Tailwind CSS",
    overview:
      "An online learning platform designed to provide courses, educational content, and user interaction through a modern UI.",
    features: [
      "Course listing & details",
      "Responsive UI",
      "Student-friendly design",
      "Contact & enquiry forms",
    ],
    status: "Completed",
  },
  {
    title: "Travel Management System",
    tech: "Java + Spring Boot",
    overview:
      "A management system to handle trips, drivers, vehicles, and travel records efficiently.",
    features: [
      "Trip assignment & tracking",
      "Driver & vehicle management",
      "Admin dashboard",
      "Database-backed reports",
    ],
    status: "Completed",
  },
  {
    title: "ERP Software",
    tech: "Laravel + MySQL",
    overview:
      "An ERP system developed to manage internal business operations and centralized data flow.",
    features: [
      "Role-based access control",
      "Centralized data management",
      "Reporting modules",
      "Scalable system architecture",
    ],
    status: "In Progress",
  },
];

export default function Portfolio() {
  const [selectedProject, setSelectedProject] = useState(null);

  return (
    <section className="py-24 px-5 md:px-10">
      <div className="text-center mb-14">
        <p className="text-violet-400 text-sm font-medium tracking-widest uppercase mb-2">
          My Work
        </p>
        <h2 className="text-3xl md:text-4xl font-bold grad-text">Projects</h2>
        <p className="text-gray-400 max-w-2xl mx-auto mt-4 text-sm md:text-base">
          A selection of projects that reflect my skills, creativity, and
          passion for building meaningful digital experiences.
        </p>
      </div>

      <div className="max-w-6xl mx-auto grid sm:grid-cols-2 gap-6">
        {projects.map((p, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: (i % 2) * 0.08 }}
            onClick={() => setSelectedProject(p)}
            className="group cursor-pointer glass rounded-2xl p-6 hover:scale-[1.03] hover:border-violet-400/40 transition-all"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <h3 className="text-xl font-semibold">{p.title}</h3>
              <span
                className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${
                  p.status === "Completed"
                    ? "bg-green-500/10 text-green-400"
                    : "bg-amber-500/10 text-amber-400"
                }`}
              >
                {p.status}
              </span>
            </div>
            <p className="text-gray-400 text-sm">{p.tech}</p>
            <span className="grad-text font-medium mt-4 inline-flex items-center gap-1">
              View Project
              <span className="group-hover:translate-x-1 transition-transform">
                →
              </span>
            </span>
          </motion.div>
        ))}
      </div>

      {/* Animated Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
              className="bg-gray-900 max-w-lg w-full rounded-xl p-6 relative"
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close */}
              <button
                className="absolute top-3 right-3 text-gray-400 hover:text-white"
                onClick={() => setSelectedProject(null)}
              >
                ✕
              </button>

              <h3 className="text-2xl font-bold mb-2 grad-text">
                {selectedProject.title}
              </h3>
              <p className="text-sm text-gray-400 mb-4">
                {selectedProject.tech}
              </p>

              <div className="mb-4">
                <h4 className="font-semibold mb-1">Project Overview</h4>
                <p className="text-gray-300 text-sm">
                  {selectedProject.overview}
                </p>
              </div>

              <div className="mb-4">
                <h4 className="font-semibold mb-1">Features</h4>
                <ul className="list-disc list-inside text-gray-300 text-sm space-y-1">
                  {selectedProject.features.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-1">Status</h4>
                <span
                  className={`inline-block px-3 py-1 text-sm rounded-full ${
                    selectedProject.status === "Completed"
                      ? "bg-green-500/10 text-green-400"
                      : "bg-amber-500/10 text-amber-400"
                  }`}
                >
                  {selectedProject.status}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
